<script setup lang="ts">
import { computed, nextTick, ref } from 'vue';
import { Clock3, FileText, MessageSquare, ReceiptText, ShoppingBasket, UserRound, Lock, Loader2 } from 'lucide-vue-next';
import MenuPage from '../views/MenuPage.vue';
import CheckoutView from '../views/CheckoutView.vue';
import OrderTracker from '../components/orders/OrderTracker.vue';
import CartRail from '../components/cart/CartRail.vue';
import { useOrdersStore } from '../stores/orders-store';
import { useCartStore } from '../stores/cart-store';
import { useSessionStore } from '../stores/session-store';
import { useUserStore } from '../stores/user-store';
import { useAppConfig } from '../composables/use-app-config';
import { formatCurrency } from '@shared/utils/currency';
import { useNotifications } from '../composables/use-notifications';
import WaiterCallBell from '../components/waiter/WaiterCallBell.vue';

const props = defineProps<{
  currentView: 'menu' | 'details' | 'orders';
  sessionReady: boolean;
  activeOrderId: string;
  editingOrderId?: string;
}>();

const emit = defineEmits<{
  (event: 'view-change', view: 'menu' | 'details' | 'orders'): void;
  (event: 'order-success', orderId: string | number): void;
  (event: 'open-profile'): void;
  (event: 'edit-order', orderId: string): void;
  (event: 'cancel-order'): void;
  (event: 'open-table-selector'): void;
}>();

const ordersStore = useOrdersStore();
const cartStore = useCartStore();
const sessionStore = useSessionStore();
const userStore = useUserStore();
const { config: settings } = useAppConfig();
const notifications = useNotifications();

const sessionLabel = computed(() => {
  const tableCode = String(sessionStore.tableContext?.table_code || '').trim();
  if (tableCode) return `Mesa ${tableCode}`;
  if (props.sessionReady || sessionStore.sessionReady) return 'Mesa asignada';
  return 'Mesa';
});

const sessionStatus = computed(() => {
  const hasTable = String(sessionStore.tableContext?.table_code || sessionStore.tableContext?.table_id || '').trim();
  if (!props.sessionReady && !sessionStore.sessionReady) {
    return hasTable ? 'Sin sesión activa' : 'Selecciona una mesa';
  }
  const status = String(ordersStore.sessionSummary?.status || sessionStore.session?.status || '').trim().toLowerCase();
  // #1023: cover every SessionStatus value the backend can send
  // (shared/src/enfa_shared/constants.py). Unknown values must not
  // claim a live session.
  const labels: Record<string, string> = {
    active: 'Activa',
    awaiting_tip: 'Esperando propina',
    awaiting_payment: 'Esperando pago',
    awaiting_payment_confirmation: 'Confirmando pago',
    partially_paid: 'Pago parcial',
    paid: 'Pagada',
    closed: 'Cerrada',
    merged: 'Fusionada',
    cancelled: 'Cancelada',
    abandoned: 'Abandonada',
    invalidated: 'Invalidada',
    receipt_available: 'Recibo disponible',
    feedback_pending: 'Pendiente de opinión',
    completed: 'Completada',
    expired: 'Expirada',
  };
  return labels[status] || status.charAt(0).toUpperCase() + status.slice(1);
});

const sessionHasBalance = computed(() => Number(ordersStore.sessionSummary?.total_amount || 0) > 0);
const hasOrderContext = computed(() => ordersStore.hasActiveOrders || sessionHasBalance.value);
const paymentEnabled = computed(() => props.sessionReady && hasOrderContext.value);
const feedbackEnabled = computed(() => props.sessionReady && hasOrderContext.value);
const invoiceEnabled = computed(() => props.sessionReady && sessionHasBalance.value);
const payableAmount = computed(() => {
  const sessionTotal = Number(ordersStore.sessionSummary?.total_amount || 0);
  return sessionTotal > 0 ? sessionTotal : cartStore.totalPrice;
});

const getDisabledReason = (service: 'payments' | 'feedback' | 'invoice') => {
  if (!props.sessionReady && !sessionStore.sessionReady) {
    return 'abre una mesa escaneando el código QR';
  }
  if (service === 'invoice') {
    return 'debes tener consumos registrados en tu mesa';
  }
  return 'debes realizar un pedido primero';
};

const handleDisabledClick = (event: MouseEvent, service: 'payments' | 'feedback' | 'invoice', serviceLabel: string) => {
  event.preventDefault();
  const reason = getDisabledReason(service);
  notifications.warning(`Para acceder a ${serviceLabel}, ${reason}.`);
};

const menuPageRef = ref<InstanceType<typeof MenuPage> | null>(null);
const checkoutViewRef = ref<InstanceType<typeof CheckoutView> | null>(null);

// #980 (C8): on the checkout view the table control is embedded, so focus it.
// #1034: elsewhere App.vue opens the dismissible TableSelectorModal over the
// current view (no navigation), so there is no pending focus to carry over.
const handleOpenTableSelector = () => {
  if (props.currentView === 'details') {
    void checkoutViewRef.value?.focusTableSelector();
    return;
  }
  emit('open-table-selector');
};

const handleEditCartItem = (item: any, index: number) => {
  if (props.currentView !== 'menu') {
    emit('view-change', 'menu');
  }
  nextTick(() => {
    menuPageRef.value?.openEditModal(item, index);
  });
};

const handleSummaryClick = (section: string) => {
  if (section === 'items') {
    emit('view-change', 'menu');
  } else if (section === 'orders' && ordersStore.activeOrdersCount > 0) {
    emit('view-change', 'orders');
  }
  // subtotal, orders (no active) — no action needed
};

const handlePaymentsClick = (event: MouseEvent) => {
  if (!paymentEnabled.value) {
    handleDisabledClick(event, 'payments', 'Pagos');
    return;
  }
  if (ordersStore.hasActiveOrders) {
    emit('view-change', 'orders');
  } else {
    emit('view-change', 'details');
  }
};
</script>

<template>
  <section
    class="web-client-screen"
    data-client-screen="web"
    data-testid="client-web-screen"
  >
    <a href="#main" class="skip-link">Saltar al contenido principal</a>
    <header class="web-client-screen__header" aria-label="Menú del restaurante">
      <div>
        <span class="header-brand">{{ settings.restaurant_name || 'Restaurante' }}</span>
        <h1>Menú y pedidos</h1>
      </div>
      <nav aria-label="Cliente web">
        <button
          type="button"
          class="web-client-screen__nav-button"
          :class="{ active: currentView === 'menu' }"
          @click="emit('view-change', 'menu')"
        >
          Menú
        </button>
        <button
          type="button"
          class="web-client-screen__nav-button"
          :class="{ active: currentView === 'details' }"
          @click="emit('view-change', 'details')"
        >
          Pedido
        </button>
        <button
          type="button"
          class="web-client-screen__nav-button"
          :class="{ active: currentView === 'orders' }"
          :disabled="!ordersStore.hasActiveOrders"
          :aria-disabled="!ordersStore.hasActiveOrders"
          :aria-label="ordersStore.hasActiveOrders ? 'Estado del pedido' : 'Estado del pedido — no disponible'"
          @click="ordersStore.hasActiveOrders && emit('view-change', 'orders')"
        >
          <ReceiptText :size="16" />
          Estado
        </button>
        <button
          type="button"
          class="web-client-screen__profile-button"
          :class="{ active: userStore.isAuthenticated }"
          :aria-pressed="userStore.isAuthenticated"
          :aria-label="userStore.isAuthenticated ? 'Perfil' : 'Iniciar sesión'"
          @click="emit('open-profile')"
        >
          <UserRound :size="16" />
          Perfil
        </button>
      </nav>
    </header>

    <main
      id="main"
      tabindex="-1"
      class="web-client-screen__layout"
      :class="{ 'web-client-screen__layout--status': currentView === 'orders' }"
    >
      <section class="web-client-screen__content">
        <!-- Sticky summary bar -->
        <section class="web-client-screen__sticky-summary" aria-label="Resumen de pedido web" role="list">
          <div class="web-client-screen__summary-item" role="listitem" tabindex="0" @click="handleSummaryClick('items')" @keydown.enter="handleSummaryClick('items')">
            <ShoppingBasket :size="20" />
            <span>Productos</span>
            <strong>{{ cartStore.totalItems }}</strong>
          </div>
          <div class="web-client-screen__summary-item" role="listitem" tabindex="0" @click="handleSummaryClick('subtotal')" @keydown.enter="handleSummaryClick('subtotal')">
            <ReceiptText :size="20" />
            <span>Subtotal</span>
            <strong>{{ formatCurrency(cartStore.totalPrice) }}</strong>
          </div>
          <div class="web-client-screen__summary-item" role="listitem" tabindex="0" @click="handleSummaryClick('orders')" @keydown.enter="handleSummaryClick('orders')">
            <Clock3 :size="20" />
            <span>Órdenes activas</span>
            <strong>{{ ordersStore.activeOrdersCount }}</strong>
          </div>
          <div class="web-client-screen__summary-item">
            <UserRound :size="20" />
            <span>{{ sessionLabel }}</span>
            <button
              v-if="!sessionReady"
              type="button"
              class="web-client-screen__summary-item__action"
              @click="handleOpenTableSelector"
            >
              Selecciona una mesa
            </button>
            <strong v-else>{{ sessionStatus }}</strong>
          </div>
        </section>

        <!-- Session expiration notice banner -->
        <div v-if="sessionStore.sessionExpiredNotice" class="web-client-screen__session-expiry-banner">
          <span class="web-client-screen__expiry-icon">⚠️</span>
          <span>{{ sessionStore.sessionExpiredNotice }}</span>
          <button type="button" class="web-client-screen__expiry-dismiss" @click="sessionStore.sessionExpiredNotice = null">✕</button>
        </div>
        <div v-if="currentView === 'menu' && sessionStore.loading && !sessionStore.bootstrapCompleted" class="web-client-screen__loading-overlay">
          <Loader2 class="web-client-screen__spinner" :size="32" />
          <p>Preparando tu experiencia...</p>
        </div>
        <MenuPage v-show="currentView === 'menu'" ref="menuPageRef" />
        <CheckoutView
          v-if="currentView === 'details'"
          ref="checkoutViewRef"
          :session-ready="sessionReady"
          :editing-order-id="editingOrderId"
          @back="emit('view-change', 'menu')"
          @success="emit('order-success', $event)"
          @open-profile="emit('open-profile')"
          @open-table-selector="handleOpenTableSelector"
          @edit-item="handleEditCartItem"
        />
        <OrderTracker
          v-else-if="currentView === 'orders'"
          :active-order-id="activeOrderId"
          @edit-order="emit('edit-order', $event)"
          @cancel-order="emit('cancel-order')"
        />
      </section>

      <aside class="web-client-screen__aside">
        <CartRail
          v-if="currentView !== 'details' && currentView !== 'orders'"
          title="Carrito lateral"
          @checkout="emit('view-change', 'details')"
          @edit-item="handleEditCartItem"
        />
        <section class="web-client-screen__services">
          <header>
            <h2>Servicios</h2>
            <span>{{ sessionReady ? 'Sesión activa' : 'Disponible al abrir mesa' }}</span>
          </header>
          <button
            type="button"
            class="web-client-screen__service-card"
            :class="{ 'web-client-screen__service-card--disabled': !paymentEnabled }"
            :aria-disabled="!paymentEnabled"
            data-client-service="payments"
            @click="handlePaymentsClick"
          >
            <Lock v-if="!paymentEnabled" :size="18" />
            <ReceiptText v-else :size="18" />
            <span>Pagos</span>
            <strong v-if="paymentEnabled">{{ formatCurrency(payableAmount) }}</strong>
          </button>
          <button
            type="button"
            class="web-client-screen__service-card"
            :class="{ 'web-client-screen__service-card--disabled': !feedbackEnabled }"
            :aria-disabled="!feedbackEnabled"
            data-client-service="feedback"
            @click="feedbackEnabled ? notifications.info('¡Gracias por tu preferencia! Puedes compartir tu opinión directamente con tu mesero.') : handleDisabledClick($event, 'feedback', 'Feedback')"
          >
            <Lock v-if="!feedbackEnabled" :size="18" />
            <MessageSquare v-else :size="18" />
            <span>Feedback</span>
            <strong v-if="feedbackEnabled">Disponible</strong>
          </button>
          <button
            type="button"
            class="web-client-screen__service-card"
            :class="{ 'web-client-screen__service-card--disabled': !invoiceEnabled }"
            data-client-service="invoice"
            @click="invoiceEnabled ? notifications.info('Servicio CFDI: Para emitir tu factura electrónica, solicita tu ticket en caja.') : handleDisabledClick($event, 'invoice', 'Factura')"
          >
            <Lock v-if="!invoiceEnabled" :size="18" />
            <FileText v-else :size="18" />
            <span>Factura</span>
            <strong v-if="invoiceEnabled">Solicitar CFDI</strong>
          </button>
        </section>
      </aside>
    </main>

    <footer class="web-client-screen__footer">
      <div class="web-client-screen__footer-content">
        <span class="web-client-screen__footer-brand">ENFA</span>
        <span class="web-client-screen__footer-copyright">© 2026 ENFA Restaurant Management System</span>
      </div>
    </footer>

    <WaiterCallBell />
  </section>
</template>

<style scoped>
.web-client-screen {
  --client-accent: #c2410c;
  --client-accent-strong: #9a3412;
  --client-accent-soft: #fed7aa;
  box-sizing: border-box;
  height: 100vh;
  height: 100dvh;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  background:
    linear-gradient(90deg, rgba(249, 115, 22, 0.06) 1px, transparent 1px),
    linear-gradient(0deg, rgba(249, 115, 22, 0.05) 1px, transparent 1px),
    #fff7ed;
  background-size: 28px 28px;
}

.web-client-screen__header {
  position: sticky;
  top: 0;
  z-index: 300;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.85rem 1.5rem;
  background: rgba(255, 255, 255, 0.98);
  border-bottom: 1px solid #d8dee8;
  backdrop-filter: blur(14px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.web-client-screen__header span {
  color: var(--client-accent-strong);
  font-size: 0.76rem;
  font-weight: 850;
  text-transform: uppercase;
}

.web-client-screen__header h1 {
  margin: 0.1rem 0 0;
  color: var(--color-text, #0f172a);
  font-size: var(--text-2xl, 1.5rem);
  font-weight: var(--font-bold, 700);
}

.header-brand {
  color: var(--color-primary, #e05a2a);
  font-size: var(--text-xs, 0.76rem);
  font-weight: 850;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.web-client-screen__header nav {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.web-client-screen__nav-button,
.web-client-screen__profile-button,
.web-client-screen__service-card {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  /* #832: WCAG 2.5.5 minimum touch target */
  min-height: 44px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 0 0.9rem;
  color: #172033;
  background: #fff;
  font-weight: 800;
  text-decoration: none;
  cursor: pointer;
}

.web-client-screen__nav-button.active {
  color: #fff;
  border-color: var(--client-accent-strong);
  background: var(--client-accent);
}

.web-client-screen__nav-button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.web-client-screen__profile-button {
  border-color: #172033;
  color: #fff;
  background: #172033;
}

.web-client-screen__profile-button.active {
  border-color: var(--client-accent-strong);
  background: var(--client-accent);
}

.web-client-screen__sticky-summary {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;
  width: 100%;
  max-width: min(1440px, calc(100vw - 2rem));
  margin: 0 auto;
  position: sticky;
  /* #1045: sticky inside .web-client-screen__content (its scroll container);
     the page header is outside it, so any offset leaves a gap above the summary. */
  top: 0;
  z-index: 200;
  background: rgba(255, 247, 237, 1);
  padding: 0.75rem 0 0.25rem;
  border-bottom: 1px solid #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  contain: layout style;
}

.web-client-screen__summary-item {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  grid-template-areas:
    "icon label"
    "icon value";
  align-items: center;
  column-gap: 0.75rem;
  min-height: 76px;
  padding: 0.85rem 1rem;
  border: 1px solid #d8dee8;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.08);
}

.web-client-screen__summary-item svg {
  grid-area: icon;
  color: var(--client-accent-strong);
}

.web-client-screen__summary-item span {
  grid-area: label;
  min-width: 0;
  color: #64748b;
  font-size: 0.76rem;
  font-weight: 850;
  text-transform: uppercase;
}

.web-client-screen__summary-item strong {
  grid-area: value;
  min-width: 0;
  overflow: hidden;
  color: #172033;
  font-size: clamp(1rem, 1.6vw, 1.35rem);
  font-weight: 900;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.web-client-screen__summary-item__action {
  grid-area: value;
  min-width: 0;
  overflow: hidden;
  color: var(--client-accent);
  font-size: clamp(1rem, 1.6vw, 1.35rem);
  font-weight: 900;
  text-decoration: none;
  background: none;
  border: none;
  cursor: pointer;
  text-align: right;
  padding: 0;
}

.web-client-screen__summary-item__action:hover {
  color: var(--client-accent-strong);
  text-decoration: underline;
}

.web-client-screen__layout {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1rem;
  width: min(1440px, calc(100vw - 2rem));
  margin: 0 auto;
  padding: 1rem 0;
  flex: 1 1 auto;
  min-height: 0;
}

.web-client-screen__layout--status {
  grid-template-columns: minmax(0, 1fr);
}

.web-client-screen__content {
  grid-column: span 3;
  min-width: 0;
  overflow-y: auto;
  overflow-x: hidden;
  border: 1px solid #d8dee8;
  border-radius: 8px;
  background: #fff;
  -webkit-overflow-scrolling: touch;
  z-index: 1;
}

.web-client-screen__layout--status .web-client-screen__content {
  grid-column: span 1;
}

.web-client-screen__aside {
  /* #100: query container for the services cards. A container query matches the
     nearest ANCESTOR container, so it must be declared here, not on __services. */
  container-type: inline-size;
  grid-column: span 1;
  display: flex;
  flex-direction: column;
  align-self: flex-start;
  position: sticky;
  top: 5rem;
  gap: 1.25rem;
  min-width: 0;
}

.web-client-screen__services {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.55rem;
  padding: 1rem;
  background: #fff;
  border: 1px solid #d8dee8;
  border-radius: 8px;
  flex-shrink: 0;
  position: static !important;
  top: auto !important;
  bottom: auto !important;
  z-index: 1;
  margin-top: 0 !important;
  transform: none !important;
  width: 100%;
  box-sizing: border-box;
}

.web-client-screen__services header {
  grid-column: 1 / -1;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
}

.web-client-screen__services h2 {
  margin: 0;
  color: #172033;
  font-size: 1rem;
}

.web-client-screen__services header span {
  color: #64748b;
  font-size: 0.76rem;
  font-weight: 800;
}

.web-client-screen__services a,
.web-client-screen__services button {
  min-height: 54px;
  padding: 0.75rem;
}

/* After the base rule: same specificity, so source order decides. */
@container (min-width: 481px) {
  .web-client-screen__services {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .web-client-screen__services a,
  .web-client-screen__services button {
    min-height: 80px;
  }
}

.web-client-screen__service-card {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  grid-template-areas:
    "icon title"
    "icon state";
  column-gap: 0.75rem;
  row-gap: 0.2rem;
  align-content: center;
  align-items: center;
  text-align: left;
}

.web-client-screen__service-card svg {
  grid-area: icon;
  color: var(--client-accent-strong);
  flex-shrink: 0;
}

.web-client-screen__service-card span {
  grid-area: title;
  color: #172033;
  font-size: 0.86rem;
  font-weight: 900;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.web-client-screen__service-card strong {
  grid-area: state;
  min-width: 0;
  color: #64748b;
  font-size: 0.78rem;
  line-height: 1.25;
  font-weight: 850;
  white-space: normal;
  overflow-wrap: break-word;
  word-break: normal;
}

.web-client-screen__service-card--disabled,
.web-client-screen__service-card:disabled {
  color: #64748b;
  background: #f1f5f9;
  cursor: not-allowed;
  opacity: 0.72;
}

@media (max-width: 980px) {
  .web-client-screen__header {
    align-items: flex-start;
    flex-direction: column;
  }

  .web-client-screen__header nav {
    width: 100%;
    overflow-x: auto;
    padding-bottom: 0.15rem;
  }

  /* #1054: target canonical sticky summary class so columns collapse properly */
  .web-client-screen__sticky-summary,
  .web-client-screen__summary {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .web-client-screen__layout {
    grid-template-columns: 1fr;
    grid-template-rows: minmax(320px, 1fr) auto;
  }

  /* #1054: prevent clientHeight=0 collapse; ensure 1-column span and minimum height */
  .web-client-screen__content {
    grid-column: 1;
    min-height: 320px;
    height: 100%;
  }

  /* #1054: lateral cart and services appear below the menu, not stealing all vertical space */
  .web-client-screen__aside {
    order: 2;
    position: static;
  }
}

@media (max-width: 560px) {
  .web-client-screen__sticky-summary,
  .web-client-screen__summary {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .web-client-screen__services {
    grid-template-columns: 1fr;
  }
}

.web-client-screen__loading-overlay {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  gap: 1rem;
  color: #64748b;
  font-size: 1rem;
  font-weight: 600;
}

.web-client-screen__spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.web-client-screen__footer {
  margin-top: auto;
  padding: 1.25rem 1.5rem;
  text-align: center;
  border-top: 1px solid #d8dee8;
  background: #ffffff;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.03);
}

.web-client-screen__footer-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  max-width: 1440px;
  margin: 0 auto;
}

.web-client-screen__footer-brand {
  font-weight: 900;
  color: var(--client-accent-strong);
}

.web-client-screen__footer-copyright {
  color: #64748b;
  font-size: 0.78rem;
}
</style>
